package org.vreo.devicehub.features.deviceDetails.data.repository

import androidx.compose.ui.util.fastFirstOrNull
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.async
import kotlinx.coroutines.withContext

import org.vreo.devicehub.features.core.data.appLogger.AppLogger
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.EmptyResult
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.device.device.AnprDevice
import org.vreo.devicehub.features.core.domain.device.device.networking.RemoteDeviceControl
import org.vreo.devicehub.features.device.data.mappers.toAnprDevice
import org.vreo.devicehub.features.device.data.mappers.toAnprDeviceRequestDto
import org.vreo.devicehub.features.core.domain.device.device.networking.RemoteDeviceDataSource
import org.vreo.devicehub.features.deviceDetails.domain.DeviceDetailsRepository

/**
 * Default [DeviceDetailsRepository] implementation. Combines device details from the backend
 * REST API with details only available directly from the device (e.g. MAC address).
 *
 * @property deviceDataSource backend data source used to fetch the user's devices
 * @property remoteDeviceControl on-device control channel used to fetch device configuration
 */
class DeviceDetailsRepositoryDefault(
    private val deviceDataSource: RemoteDeviceDataSource,
    private val remoteDeviceControl: RemoteDeviceControl,
) : DeviceDetailsRepository {
    /**
     * Fetches device details from the backend and, when available, merges in device-only fields
     * (local IP address, MAC address) obtained directly from the device. Both calls run
     * concurrently; the backend result is authoritative and is returned as-is if the on-device
     * call fails or does not resolve.
     *
     * @param token user authentication token
     * @param deviceId device ID
     * @return Result of merged device details as [AnprDevice] on success, or the backend error on failure
     */
    override suspend fun getDeviceDetails(
        token: String,
        deviceId: String
    ): Result<AnprDevice, DataError.Remote> {
        // Some device details are available via backend REST API server, such as TAG, and other are only available
        // on device, such as MAC address. To get all required details we need to make to calls and combine results.
        // Primary results is from the backend service.
        return withContext(Dispatchers.Default) {
            val backendResultsDeferred = async {
                try {
                    when (val result = deviceDataSource.getUserDevices(token)) {
                        is Result.Success -> {
                            val resultData = result.data.fastFirstOrNull { it.id == deviceId }
                            if (resultData == null) {
                                Result.Error(DataError.Remote.NOT_FOUND)
                            } else {
                                Result.Success(resultData.toAnprDevice())
                            }
                        }

                        is Result.Error -> {
                            Result.Error(result.error)
                        }
                    }
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Exception) {
                    AppLogger.error("Failed to get user devices, error: ${e.message}")
                    Result.Error(DataError.Remote.UNKNOWN)
                }
            }

            val deviceResultDeferred = async {
                try {
                    val result = remoteDeviceControl.getDeviceConfiguration(
                        token,
                        deviceId,
                    )

                    when (result) {
                        is Result.Success -> {
                            result.data
                        }

                        is Result.Error -> {
                            null
                        }
                    }
                } catch (e: CancellationException) {
                    throw e
                } catch (_: Exception) {
                    null
                }
            }

            val backendResults = backendResultsDeferred.await()
            val deviceResult = deviceResultDeferred.await()

            when (backendResults) {
                is Result.Success -> {
                    if (deviceResult == null) {
                        Result.Success(backendResults.data)
                    } else {
                        Result.Success(
                            backendResults.data.copy(
                                localIpAddress = deviceResult.localIpAddress,
                                macAddress = deviceResult.macAddress,
                            )
                        )
                    }
                }

                is Result.Error -> {
                    backendResults
                }
            }
        }
    }

    /**
     * Sends updated device details to the backend.
     *
     * @param token user authentication token
     * @param details device details to persist
     * @return empty Result on success, or the backend error on failure
     */
    override suspend fun updateDeviceDetails(
        token: String,
        details: AnprDevice
    ): EmptyResult<DataError.Remote> {
        return withContext(Dispatchers.Default) {
            deviceDataSource.updateDeviceDetails(
                token,
                details.toAnprDeviceRequestDto()
            )
        }
    }

    /**
     * Unlinks the selected device from the current user's account via the backend.
     *
     * @param token user authentication token
     * @param deviceId device ID to unlink
     * @return empty Result on success, or the backend error on failure
     */
    override suspend fun unlinkDevice(
        token: String,
        deviceId: String
    ): EmptyResult<DataError.Remote> {
        return withContext(Dispatchers.Default) {
            deviceDataSource.removeDeviceFromAccount(
                token,
                deviceId
            )
        }
    }

    /**
     * Permanently deletes the selected device from the production device registry via the backend.
     *
     * @param token user authentication token
     * @param deviceId device ID to delete
     * @return empty Result on success, or the backend error on failure
     */
    override suspend fun deleteDeviceFromProduction(
        token: String,
        deviceId: String
    ): EmptyResult<DataError.Remote> {
        return withContext(Dispatchers.Default) {
            deviceDataSource.deleteDeviceFromProduction(
                token,
                deviceId
            )
        }
    }
}